import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Check, X, Trash2, Star, Plus, Send, MessageSquare } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL;

function headers(json) {
  var t = localStorage.getItem('admin_token');
  var h = t ? { Authorization: 'Bearer ' + t } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

var FILTERS = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' }
];

// Approve, edit, answer or write reviews without opening Sanity Studio.
export default function ReviewsPanel() {
  var [rows, setRows] = useState([]);
  var [counts, setCounts] = useState({});
  var [status, setStatus] = useState('pending');
  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState('');
  var [msg, setMsg] = useState(null);
  var [edit, setEdit] = useState({});
  var [targets, setTargets] = useState([]);
  var [showNew, setShowNew] = useState(false);
  var [drip, setDrip] = useState(null);

  var load = useCallback(async function() {
    setLoading(true);
    try {
      var r = await fetch(API + '/api/admin/reviews?status=' + status, { headers: headers() });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not load reviews');
      setRows(d.reviews || []);
      setCounts(d.counts || {});
      setEdit({});
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(function() { load(); }, [load]);

  useEffect(function() {
    fetch(API + '/api/admin/review-targets', { headers: headers() })
      .then(function(r) { return r.json(); })
      .then(function(d) { setTargets(d.targets || []); })
      .catch(function() {});
    fetch(API + '/api/admin/review-drip-status', { headers: headers() })
      .then(function(r) { return r.json(); })
      .then(setDrip)
      .catch(function() {});
  }, []);

  async function patch(id, body, note) {
    setBusy(id); setMsg(null);
    try {
      var r = await fetch(API + '/api/admin/reviews/' + encodeURIComponent(id), {
        method: 'PATCH', headers: headers(true), body: JSON.stringify(body)
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Update failed');
      setMsg({ type: 'success', text: note || 'Saved' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  async function remove(id) {
    if (!window.confirm('Delete this review for good?')) return;
    setBusy(id); setMsg(null);
    try {
      var r = await fetch(API + '/api/admin/reviews/' + encodeURIComponent(id), {
        method: 'DELETE', headers: headers()
      });
      if (!r.ok) throw new Error('Delete failed');
      setMsg({ type: 'success', text: 'Review deleted' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  async function runDrip() {
    setBusy('drip'); setMsg(null);
    try {
      var r = await fetch(API + '/api/admin/run-review-drip-due', { method: 'POST', headers: headers() });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not run the drip');
      setMsg({
        type: 'success',
        text: d.skipped === 'preview_env'
          ? 'Skipped — real sends only run on the live site (' + d.due + ' due there).'
          : 'Sent ' + (d.sent || 0) + ' invitation(s). ' + (d.due_after || 0) + ' still due.'
      });
      var s = await fetch(API + '/api/admin/review-drip-status', { headers: headers() }).then(function(x) { return x.json(); });
      setDrip(s);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally {
      setBusy('');
    }
  }

  function fieldOf(row, key) {
    var e = edit[row.id] || {};
    return e[key] != null ? e[key] : row[key];
  }

  function setField(id, key, value) {
    setEdit(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = Object.assign({}, next[id] || {}, { [key]: value });
      return next;
    });
  }

  return (
    <div className="space-y-5" data-testid="reviews-panel">
      {drip ? (
        <div className="bg-neutral-900 border border-white/10 rounded-2xl p-5 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-white font-semibold">Review invitations</h3>
            <p className="text-neutral-500 text-sm mt-1">
              Runs by itself every day: customers are asked {drip.delay_days} days after their purchase.
              {' '}<span className="text-neutral-300">{drip.due_now || 0} due right now</span>, {drip.remaining_backlog || 0} older orders never asked.
            </p>
          </div>
          <button onClick={runDrip} disabled={busy === 'drip'} data-testid="review-drip-run-due"
                  className="flex items-center gap-2 bg-[#d4af37] text-black font-semibold text-sm rounded-full px-4 py-2 hover:bg-white disabled:opacity-40">
            {busy === 'drip' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send due now
          </button>
        </div>
      ) : null}

      <div className="bg-neutral-900 border border-white/10 rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {FILTERS.map(function(f) {
            var n = counts[f.id];
            var on = status === f.id;
            return (
              <button key={f.id} onClick={function() { setStatus(f.id); }}
                      data-testid={'reviews-filter-' + f.id}
                      className={'text-xs rounded-full px-3.5 py-1.5 border transition-colors ' +
                        (on ? 'bg-[#d4af37] text-black border-[#d4af37] font-semibold' : 'text-neutral-300 border-white/15 hover:border-white/40')}>
                {f.label}{n != null ? ' (' + n + ')' : ''}
              </button>
            );
          })}
          <button onClick={load} data-testid="reviews-refresh"
                  className="ml-auto flex items-center gap-1.5 text-xs text-neutral-300 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5">
            <RefreshCw className={'w-3.5 h-3.5' + (loading ? ' animate-spin' : '')} /> Refresh
          </button>
          <button onClick={function() { setShowNew(!showNew); }} data-testid="reviews-add-toggle"
                  className="flex items-center gap-1.5 text-xs bg-white/5 text-neutral-200 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5">
            <Plus className="w-3.5 h-3.5" /> Add a review
          </button>
        </div>

        {showNew ? <NewReviewForm targets={targets} onDone={function() { setShowNew(false); load(); }} /> : null}

        {loading ? (
          <p className="py-10 text-center text-neutral-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </p>
        ) : rows.length ? (
          <div className="space-y-3" data-testid="reviews-rows">
            {rows.map(function(row) {
              var pending = edit[row.id];
              var dirty = !!pending;
              var rowBusy = busy === row.id;
              var rating = fieldOf(row, 'rating');
              var nameVal = fieldOf(row, 'name');
              var textVal = fieldOf(row, 'text');
              var replyVal = fieldOf(row, 'reply');
              var dateVal = row.review_date ? row.review_date.slice(0, 10) : '';
              return (
                <div key={row.id} className="bg-black border border-white/10 rounded-xl p-4" data-testid="review-row">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className={'text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ' +
                      (row.status === 'approved' ? 'text-emerald-300 border-emerald-500/40'
                        : row.status === 'rejected' ? 'text-red-300 border-red-500/40'
                        : 'text-amber-300 border-amber-500/40')}>{row.status}</span>
                    <span className="text-neutral-300 text-sm truncate max-w-[45%]" title={row.product_slug}>
                      {row.product_name}
                    </span>
                    <span className="text-neutral-600 text-xs">{dateVal}</span>
                    <span className="text-neutral-600 text-xs truncate">{row.email}</span>
                    <div className="ml-auto flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(function(s) {
                        return (
                          <button key={s} onClick={function() { setField(row.id, 'rating', s); }}
                                  data-testid={'review-set-rating-' + s}>
                            <Star className={'w-4 h-4 ' + (s <= rating ? 'fill-[#d4af37] text-[#d4af37]' : 'text-neutral-700')} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <input value={nameVal} data-testid="review-edit-name"
                         onChange={function(e) { setField(row.id, 'name', e.target.value); }}
                         className="w-full sm:w-72 bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-2 focus:outline-none focus:border-[#d4af37]" />
                  <textarea value={textVal} rows={3} data-testid="review-edit-text"
                            onChange={function(e) { setField(row.id, 'text', e.target.value); }}
                            className="w-full bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-neutral-200 focus:outline-none focus:border-[#d4af37]" />
                  <div className="flex items-start gap-2 mt-2">
                    <MessageSquare className="w-4 h-4 text-neutral-600 mt-2.5" />
                    <textarea value={replyVal} rows={2} placeholder="Public reply (optional)"
                              data-testid="review-edit-reply"
                              onChange={function(e) { setField(row.id, 'reply', e.target.value); }}
                              className="flex-1 bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-neutral-300 placeholder-neutral-700 focus:outline-none focus:border-[#d4af37]" />
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {row.status !== 'approved' ? (
                      <button onClick={function() { patch(row.id, Object.assign({ status: 'approved' }, pending || {}), 'Approved — it is live now'); }}
                              disabled={rowBusy} data-testid="review-approve"
                              className="flex items-center gap-1.5 text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 rounded-full px-3 py-1.5">
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                    ) : null}
                    {row.status !== 'rejected' ? (
                      <button onClick={function() { patch(row.id, { status: 'rejected' }, 'Rejected — hidden from the site'); }}
                              disabled={rowBusy} data-testid="review-reject"
                              className="flex items-center gap-1.5 text-xs text-neutral-300 border border-white/15 hover:border-white/40 rounded-full px-3 py-1.5">
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    ) : null}
                    {dirty ? (
                      <button onClick={function() { patch(row.id, pending, 'Changes saved'); }}
                              disabled={rowBusy} data-testid="review-save"
                              className="flex items-center gap-1.5 text-xs bg-[#d4af37] text-black font-semibold rounded-full px-3 py-1.5 hover:bg-white">
                        {rowBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save changes
                      </button>
                    ) : null}
                    <button onClick={function() { remove(row.id); }} disabled={rowBusy}
                            data-testid="review-delete"
                            className="ml-auto flex items-center gap-1.5 text-xs text-red-300/80 hover:text-red-300 border border-red-500/20 hover:border-red-500/50 rounded-full px-3 py-1.5">
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="py-10 text-center text-neutral-600 text-sm" data-testid="reviews-empty">Nothing here.</p>
        )}

        {msg ? (
          <div data-testid="reviews-message"
               className={'mt-4 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success'
                 ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                 : 'bg-red-500/10 border border-red-500/30 text-red-300')}>
            {(msg.type === 'success' ? '✅ ' : '⚠️ ') + msg.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function NewReviewForm(props) {
  // NOTE: never call .map() on a member expression (props.x.map) — the
  // visual-edits babel plugin recurses forever on it. Copy to a local first.
  var targetList = props.targets || [];
  var [form, setForm] = useState({ product_slug: '', name: '', rating: 5, text: '', source: 'Verified Purchase', status: 'approved' });
  var [saving, setSaving] = useState(false);
  var [err, setErr] = useState('');

  function set(key, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [key]: value }); });
  }

  async function submit() {
    if (!form.product_slug || !form.name.trim() || !form.text.trim()) {
      setErr('Pick a product and fill in the name and the review');
      return;
    }
    setSaving(true); setErr('');
    try {
      var r = await fetch(API + '/api/admin/reviews', {
        method: 'POST', headers: headers(true), body: JSON.stringify(form)
      });
      var d = await r.json().catch(function() { return {}; });
      if (!r.ok) throw new Error(d.detail || 'Could not save');
      props.onDone();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-black border border-[#d4af37]/30 rounded-xl p-4 mb-5" data-testid="review-new-form">
      <p className="text-white text-sm mb-3">Add a review you received by email or message</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <select value={form.product_slug} onChange={function(e) { set('product_slug', e.target.value); }}
                data-testid="review-new-target"
                className="bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">— Product or general pool —</option>
          {targetList.map(function(t) {
            return <option key={t.slug} value={t.slug}>{t.name}</option>;
          })}
        </select>
        <input placeholder="Reviewer name" value={form.name} data-testid="review-new-name"
               onChange={function(e) { set('name', e.target.value); }}
               className="bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
      </div>
      <textarea placeholder="What they said" rows={3} value={form.text} data-testid="review-new-text"
                onChange={function(e) { set('text', e.target.value); }}
                className="w-full mt-2 bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(function(s) {
            return (
              <button key={s} onClick={function() { set('rating', s); }} data-testid={'review-new-star-' + s}>
                <Star className={'w-4 h-4 ' + (s <= form.rating ? 'fill-[#d4af37] text-[#d4af37]' : 'text-neutral-700')} />
              </button>
            );
          })}
        </div>
        <select value={form.source} onChange={function(e) { set('source', e.target.value); }}
                className="bg-neutral-950 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-neutral-300">
          {['Verified Purchase', 'Website', 'Email', 'Facebook', 'Other'].map(function(s) {
            return <option key={s} value={s}>{s}</option>;
          })}
        </select>
        <button onClick={submit} disabled={saving} data-testid="review-new-save"
                className="ml-auto flex items-center gap-1.5 text-xs bg-[#d4af37] text-black font-semibold rounded-full px-4 py-2 hover:bg-white disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Publish review
        </button>
      </div>
      {err ? <p className="text-red-300 text-xs mt-2">{err}</p> : null}
    </div>
  );
}
